import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchApiData } from "../../../redux/reducers/commonApiSlice";
import ModalHeadAndClose from "../../payouts-wtf/common/ModalHeadAndClose";

const SLAForm = ({
  refetchSlaRules,
  onCloseModal,
  modValues,
  setModValues,
  token,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    uid: "",
    current_status: "",
    next_status: "",
    days: 0,
    hours: 0,
    minutes: 0,
    status: "active",
  });

  // Populate form when editing
  useEffect(() => {
    if (modValues.cardName === "edit" && modValues.rowData) {
      const { rowData } = modValues;
      setFormData({
        uid: rowData.uid || "",
        current_status: rowData.current_status || "",
        next_status: rowData.next_status || "",
        days: parseInt(rowData.days) || 0,
        hours: parseInt(rowData.hours) || 0,
        minutes: parseInt(rowData.minutes) || 0,
        status: rowData.status === "active" ? "active" : "inactive",
      });
    }
  }, [modValues]);

  // Format status display
  const formatStatus = (status) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Calculate total minutes
  const calculateTotalMinutes = (days, hours, minutes) => {
    return parseInt(days) * 24 * 60 + parseInt(hours) * 60 + parseInt(minutes);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.uid) {
      toast.error("UID is required");
      return;
    }

    if (formData.days === 0 && formData.hours === 0 && formData.minutes === 0) {
      toast.error(
        "Please set at least some time limit (days, hours, or minutes)"
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        uid: formData.uid,
        current_status: formData.current_status,
        next_status: formData.next_status,
        days: parseInt(formData.days),
        hours: parseInt(formData.hours),
        minutes: parseInt(formData.minutes),
        status: formData.status,
      };

      const result = await dispatch(
        fetchApiData({
          method: "PUT",
          endpoint: "/ticketSystem/sla-rules/update",
          payload,
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message || "SLA rule updated successfully"
        );
        refetchSlaRules();
        onCloseModal();
      } else {
        toast.error(result?.payload?.message || "Failed to update SLA rule");
      }
    } catch (error) {
      console.error("Error updating SLA rule:", error);
      toast.error("Failed to update SLA rule");
    } finally {
      setLoading(false);
    }
  };

  //   // Reset form
  //   const handleReset = () => {
  //     if (modValues.rowData) {
  //       const { rowData } = modValues;
  //       setFormData({
  //         uid: rowData.uid || "",
  //         current_status: rowData.current_status || "",
  //         next_status: rowData.next_status || "",
  //         days: parseInt(rowData.days) || 0,
  //         hours: parseInt(rowData.hours) || 0,
  //         minutes: parseInt(rowData.minutes) || 0,
  //         status: rowData.status === "true" || rowData.status === true,
  //       });
  //     }
  //   };

  const totalMinutes = calculateTotalMinutes(
    formData.days,
    formData.hours,
    formData.minutes
  );

  return (
    <div className="sla-form-container">
      <div style={{ padding: "20px" }}>
        <ModalHeadAndClose
          title="Edit SLA Rule"
          subtitle="Update Service Level Agreement time limits for status transitions"
          onClose={onCloseModal}
        />
      </div>

      <div className="sla-form-content">
        {/* Status Transition Display */}
        <div className="transition-info-section">
          <h6 className="section-title">Status Transition</h6>
          <div className="transition-display">
            <div className="status-flow">
              <span className="status-from">
                {formatStatus(formData.current_status)}
              </span>
              <div className="arrow">→</div>
              <span className="status-to">
                {formatStatus(formData.next_status)}
              </span>
            </div>
            <small className="transition-description">
              This rule defines the maximum time allowed for transitioning from{" "}
              <strong>{formatStatus(formData.current_status)}</strong> to{" "}
              <strong>{formatStatus(formData.next_status)}</strong>
            </small>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* Time Limit Section */}
          <div className="time-limit-section">
            <h6 className="section-title">Time Limit Configuration</h6>

            <Row className="time-inputs">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Days</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="30"
                    value={formData.days}
                    onChange={(e) => handleInputChange("days", e.target.value)}
                    className="time-input"
                  />
                  <Form.Text className="text-muted">0-30 days</Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Hours</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="23"
                    value={formData.hours}
                    onChange={(e) => handleInputChange("hours", e.target.value)}
                    className="time-input"
                  />
                  <Form.Text className="text-muted">0-23 hours</Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Minutes</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="59"
                    value={formData.minutes}
                    onChange={(e) =>
                      handleInputChange("minutes", e.target.value)
                    }
                    className="time-input"
                  />
                  <Form.Text className="text-muted">0-59 minutes</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Total Time Display */}
            <div className="total-time-display">
              <div className="total-info">
                <span className="total-label">Total Time Limit:</span>
                <span className="total-value">
                  {formData.days > 0 && `${formData.days}d `}
                  {formData.hours > 0 && `${formData.hours}h `}
                  {formData.minutes > 0 && `${formData.minutes}m`}
                  {totalMinutes === 0 && "0 minutes"}
                </span>
              </div>
              <div className="total-minutes">
                <small>({totalMinutes} total minutes)</small>
              </div>
            </div>
          </div>

          {/* Rule Status Section */}
          <div className="rule-status-section">
            <h6 className="section-title">Rule Status</h6>
            <Form.Group>
              <div className="status-toggle">
                <Form.Check
                  type="switch"
                  id="rule-status-switch"
                  label={formData.status === "active" ? "Active" : "Inactive"}
                  checked={formData.status === "active"}
                  onChange={(e) =>
                    handleInputChange(
                      "status",
                      e.target.checked ? "active" : "inactive"
                    )
                  }
                  className="status-switch"
                />
                <Form.Text className="text-muted">
                  {formData.status === "active"
                    ? "This rule is currently active and will be enforced"
                    : "This rule is inactive and will not be enforced"}
                </Form.Text>
              </div>
            </Form.Group>
          </div>

          {/* Warning/Info Messages */}
          {totalMinutes === 0 && (
            <Alert variant="warning" className="time-warning">
              <strong>Warning:</strong> Please set at least some time limit. A
              zero time limit means no SLA will be enforced for this transition.
            </Alert>
          )}

          {totalMinutes > 0 && totalMinutes < 60 && (
            <Alert variant="info" className="time-info">
              <strong>Note:</strong> Very short time limits (under 1 hour) may
              be difficult to meet in practice.
            </Alert>
          )}

          {/* Form Actions */}
          <div className="sla-form-actions">
            <div className="action-buttons">
              {/* <Button
                type="button"
                variant="outline-secondary"
                onClick={handleReset}
                disabled={loading}
                className="reset-btn"
              >
                Reset
              </Button> */}

              <Button
                type="button"
                variant="outline-danger"
                onClick={onCloseModal}
                disabled={loading}
                className="cancel-btn"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={loading || totalMinutes === 0}
                className="save-btn"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Updating...
                  </>
                ) : (
                  "Update SLA Rule"
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SLAForm;
